import React, { useEffect, useState } from 'react';
import {
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  TableContainer,
  Typography,
  CircularProgress,
  Box,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Tooltip,
  Stack,
  Select,
  MenuItem
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { API_URL } from '../../config';
import ReplayIcon from '@mui/icons-material/Replay';
import TablePagination from '@mui/material/TablePagination';
import authService from '../../utils/authService';

interface Donation {
  _id: string;
  amount: number;
  currency: string;
  status: string;
  donationType: string;
  createdAt?: string;
  category?: string;
  certificateUrl?: string;
  donator?: {
    firstName?: string;
    lastName?: string;
    companyName?: string;
    taxId?: string;
    email?: string;
    phone?: string;
    address?: string;
    wantsPrintedReceipt?: boolean;
  };
}

const statusOptions = ['ALL','SUCCESSFUL','FAILED','PENDING','REFUNDED'];

const DonationsTable: React.FC = () => {
  const [donations, setDonations] = useState<Donation[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [selectedDonation, setSelectedDonation] = useState<Donation | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [detailsOpen, setDetailsOpen] = useState(false);

  const [refundTarget, setRefundTarget] = useState<Donation | null>(null);
  const [processingRefund, setProcessingRefund] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  // Fetch donations once on mount
  useEffect(() => {
    const fetchDonations = async () => {
      try {
        const query = new URLSearchParams({ limit: '100' });
        if (statusFilter !== 'ALL') query.append('status', statusFilter);
        const res = await authService.authFetch(`${API_URL}/api/admin/donations?${query.toString()}`);

        if (res.status === 401) {
          authService.clearTokens();
          window.location.href = '/admin/login';
          return;
        }

        if (!res.ok) throw new Error('Bağışlar alınamadı');

        const data = await res.json();
        setDonations(data.donations || []);
      } catch (err: any) {
        setError(err.message ?? 'Sunucu hatası');
      } finally {
        setLoading(false);
      }
    };

    fetchDonations();
  }, [statusFilter]);

  const displayedDonations = donations.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  /* ----------------------------- RENDER -------------------------------- */
  if (loading) {
    return (
      <Box display="flex" justifyContent="center" my={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) return <Alert severity="error">{error}</Alert>;

  return (
    <>
      {/* Filter controls */}
      <Box mb={2} display="flex" alignItems="center" gap={2}>
        <Typography>Status:</Typography>
        <Select size="small" value={statusFilter} onChange={(e) => setStatusFilter((e.target as HTMLInputElement).value as string)}>
          {statusOptions.map((opt) => (
            <MenuItem key={opt} value={opt}>{opt === 'ALL' ? 'Tümü' : opt}</MenuItem>
          ))}
        </Select>
      </Box>

      <TableContainer component={Paper} sx={{ backgroundColor: '#1e1e1e' }}>
        <Typography variant="h6" sx={{ p: 2 }}>
          Bağış Listesi
        </Typography>
        <Table size="small" sx={{ '& *': { color: 'white !important', borderColor: 'rgba(255, 255, 255, 0.12) !important' } }}>
          <TableHead sx={{ '& th': { backgroundColor: '#1e1e1e', color: 'white !important' } }}>
            <TableRow>
              <TableCell>Tarih</TableCell>
              <TableCell>Order ID</TableCell>
              <TableCell>Bağışçı</TableCell>
              <TableCell>Kategori</TableCell>
              <TableCell>Tutar</TableCell>
              <TableCell>Basılı Makbuz</TableCell>
              <TableCell>Durum</TableCell>
              <TableCell>Bağış Tipi</TableCell>
              <TableCell>E-posta</TableCell>
              <TableCell>Telefon</TableCell>
              <TableCell>Adres</TableCell>
              <TableCell>Firma Ünvanı</TableCell>
              <TableCell>Vergi No</TableCell>
              <TableCell align="center" sx={{ position:'sticky', right:0, backgroundColor:'#1e1e1e' }}>İşlemler</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {displayedDonations.map((d) => (
              <TableRow key={d._id} hover sx={{ '&:nth-of-type(odd)': { backgroundColor: 'rgba(255, 255, 255, 0.05)' }, '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.1)' } }}>
                <TableCell>{new Date(d.createdAt || '').toLocaleDateString('tr-TR')}</TableCell>
                <TableCell sx={{ maxWidth: 260, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={d._id}>{d._id}</TableCell>
                <TableCell>{d.donator ? `${d.donator.firstName ?? ''} ${d.donator.lastName ?? ''}`.trim() || '-' : '-'}</TableCell>
                <TableCell>{d.category ?? '-'}</TableCell>
                <TableCell>{d.amount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} {d.currency}</TableCell>
                <TableCell>{d.donator?.wantsPrintedReceipt ? 'Evet' : 'Hayır'}</TableCell>
                <TableCell>{d.status}</TableCell>
                <TableCell>{d.donationType}</TableCell>
                <TableCell>{d.donator?.email || '-'}</TableCell>
                <TableCell>{d.donator?.phone || '-'}</TableCell>
                <TableCell sx={{ maxWidth:200, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>{d.donator?.address || '-'}</TableCell>
                <TableCell>{d.donator?.companyName || '-'}</TableCell>
                <TableCell>{d.donator?.taxId || '-'}</TableCell>
                <TableCell sx={{ position:'sticky', right:0, backgroundColor:'rgba(30,30,30,0.9)' }}>
                  <Stack direction="row" spacing={1} justifyContent="center">
                    <Tooltip title="Detaylar">
                      <IconButton size="small" onClick={() => { setSelectedDonation(d); setDetailsOpen(true); }}>
                        <InfoOutlinedIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    {d.status === 'SUCCESSFUL' && (
                      <Tooltip title="İade Et">
                        <IconButton size="small" onClick={() => setRefundTarget(d)}>
                          <ReplayIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={donations.length}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value,10)); setPage(0); }}
          rowsPerPageOptions={[10,25,50,100]}
          sx={{ '& *': { color: 'white !important' } }}
        />
      </TableContainer>

      {/* Details dialog */}
      <Dialog 
        open={detailsOpen} 
        onClose={() => setDetailsOpen(false)} 
        maxWidth="md" 
        fullWidth 
        PaperProps={{ 
          sx: { 
            backgroundColor: '#000 !important', 
            color: 'white',
            maxWidth: '100%',
            width: '100%',
            m: 2,
            '& .MuiDialog-container': {
              alignItems: 'flex-start',
              pt: 8
            }
          } 
        }}
      >
        <DialogTitle>Bağış Detayları</DialogTitle>
        <DialogContent dividers>
          {selectedDonation && (
            <Box>
              {/* Tarih */}
              <Typography variant="subtitle2">Tarih</Typography>
              <Typography>{new Date(selectedDonation.createdAt || '').toLocaleDateString('tr-TR')}</Typography>

              {/* Order ID */}
              <Typography variant="subtitle2" sx={{ mt: 1 }}>Order ID</Typography>
              <Typography sx={{ wordBreak:'break-all' }}>{selectedDonation._id}</Typography>

              {/* Durum */}
              <Typography variant="subtitle2" sx={{ mt: 1 }}>Durum</Typography>
              <Typography>{selectedDonation.status}</Typography>

              {/* Bağışçı */}
              <Typography variant="subtitle2" sx={{ mt: 1 }}>Bağışçı</Typography>
              <Typography>{`${selectedDonation.donator?.firstName ?? ''} ${selectedDonation.donator?.lastName ?? ''}`.trim() || '-'}</Typography>

              {/* Kategori */}
              <Typography variant="subtitle2" sx={{ mt: 1 }}>Kategori</Typography>
              <Typography>{selectedDonation.category ?? '-'}</Typography>

              {/* Tutar */}
              <Typography variant="subtitle2" sx={{ mt: 1 }}>Tutar</Typography>
              <Typography>{selectedDonation.amount} {selectedDonation.currency}</Typography>

              {/* Basılı Makbuz */}
              <Typography variant="subtitle2" sx={{ mt: 1 }}>Basılı Makbuz</Typography>
              <Typography>{selectedDonation.donator?.wantsPrintedReceipt ? 'Evet' : 'Hayır'}</Typography>

              {/* Bağış Tipi */}
              <Typography variant="subtitle2" sx={{ mt: 1 }}>Bağış Tipi</Typography>
              <Typography>{selectedDonation.donationType}</Typography>

              {/* E-posta */}
              <Typography variant="subtitle2" sx={{ mt: 1 }}>E-posta</Typography>
              <Typography>{selectedDonation.donator?.email ?? '-'}</Typography>

              {/* Telefon */}
              <Typography variant="subtitle2" sx={{ mt: 1 }}>Telefon</Typography>
              <Typography>{selectedDonation.donator?.phone ?? '-'}</Typography>

              {/* Adres */}
              <Typography variant="subtitle2" sx={{ mt: 1 }}>Adres</Typography>
              <Typography>{selectedDonation.donator?.address ?? '-'}</Typography>

              {/* Firma Ünvanı */}
              <Typography variant="subtitle2" sx={{ mt: 1 }}>Firma Ünvanı</Typography>
              <Typography>{selectedDonation.donator?.companyName ?? '-'}</Typography>

              {/* Vergi No */}
              <Typography variant="subtitle2" sx={{ mt: 1 }}>Vergi No</Typography>
              <Typography>{selectedDonation.donator?.taxId ?? '-'}</Typography>

              {/* Sertifika PDF */}
              {selectedDonation.certificateUrl && (
                <Box sx={{ mt: 2, '& *': { color: 'white !important' } }}>
                  <Button
                    variant="outlined"
                    href={selectedDonation.certificateUrl}
                    target="_blank"
                    download={`bagis_sertifikasi_${selectedDonation._id}.pdf`}
                    sx={{ color: 'white', borderColor: 'white', '&:hover': { borderColor: '#90caf9', color: '#90caf9' } }}
                  >
                    Fatura / Sertifika PDF
                  </Button>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsOpen(false)}>KAPAT</Button>
        </DialogActions>
      </Dialog>

      {/* Refund confirmation dialog */}
      <Dialog 
        open={Boolean(refundTarget)} 
        onClose={() => setRefundTarget(null)}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            backgroundColor: '#000 !important',
            color: 'white',
            maxWidth: '100%',
            width: '100%',
            m: 2,
            '& .MuiDialog-container': {
              alignItems: 'flex-start',
              pt: 8
            }
          }
        }}
      >
        <DialogTitle>Bağışı İade Et</DialogTitle>
        <DialogContent dividers>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <strong>Not:</strong> Bu işlem sadece bağışı sistemde "İade Edildi" olarak işaretler.
            Gerçek ödeme iadesini Payten yönetim panelinden manuel olarak yapmanız gerekmektedir.
          </Alert>
          <Typography sx={{ mb: 5 }}>
            {refundTarget?.amount} {refundTarget?.currency} tutarındaki bağışı iade etmek istediğinize emin misiniz?
          </Typography>

          {/* Tarih */}
          <Typography variant="subtitle2">Tarih</Typography>
          <Typography>{new Date(refundTarget?.createdAt || '').toLocaleDateString('tr-TR')}</Typography>

          {/* Order ID */}
          <Typography variant="subtitle2" sx={{ mt: 1 }}>Order ID</Typography>
          <Typography sx={{ wordBreak:'break-all' }}>{refundTarget?._id}</Typography>

          {/* Bağışçı */}
          <Typography variant="subtitle2" sx={{ mt: 1 }}>Bağışçı</Typography>
          <Typography>{`${refundTarget?.donator?.firstName ?? ''} ${refundTarget?.donator?.lastName ?? ''}`.trim() || '-'}</Typography>

          {processingRefund && (
            <Box display="flex" justifyContent="center" mt={2}>
              <CircularProgress size={24} />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRefundTarget(null)} disabled={processingRefund}>KAPAT</Button>
          <Button
            color="error"
            disabled={processingRefund}
            onClick={async () => {
              if (!refundTarget) return;
              setProcessingRefund(true);
              try {
                const res = await authService.authFetch(`${API_URL}/api/admin/refund`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ donationId: refundTarget._id })
                });

                if (res.status === 401) {
                  authService.clearTokens();
                  window.location.href = '/admin/login';
                  return;
                }

                if (!res.ok) {
                  const errData = await res.json();
                  alert(errData.message ?? 'İade işlemi başarısız');
                } else {
                  const updated = await res.json();
                  setDonations((prev) => prev.map((don) => don._id === updated.donation._id ? updated.donation : don));
                  setRefundTarget(null);
                }
              } catch (e) {
                alert('Sunucu hatası');
              } finally {
                setProcessingRefund(false);
              }
            }}
          >
            İade Et
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DonationsTable;
